import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FileSpreadsheet, FileText, File, Download, Search, FolderOpen, ArrowLeft, Upload, Trash2, Plus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rtr_admin_token';

const fileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet size={28} className="text-green-500" />;
  if (['pdf'].includes(ext)) return <FileText size={28} className="text-red-500" />;
  if (['doc', 'docx'].includes(ext)) return <FileText size={28} className="text-blue-500" />;
  return <File size={28} className="text-gray-400" />;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileLibrary = () => {
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'Vehicle Specs', file: null });
  const isAdmin = !!localStorage.getItem(TOKEN_KEY);
  const authHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeCategory ? `?category=${encodeURIComponent(activeCategory)}` : '';
      const { data } = await axios.get(`${API}/api/files${params}`);
      setFiles(data.data);
      setCategories(data.categories);
    } catch (err) { console.error('Failed to fetch files:', err); }
    finally { setLoading(false); }
  }, [activeCategory]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const filtered = search
    ? files.filter(f =>
        f.title.toLowerCase().includes(search.toLowerCase()) ||
        f.filename.toLowerCase().includes(search.toLowerCase()) ||
        (f.description || '').toLowerCase().includes(search.toLowerCase())
      )
    : files;

  const handleUpload = async () => {
    if (!uploadForm.file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadForm.file);
      fd.append('title', uploadForm.title || uploadForm.file.name.replace(/\.[^.]+$/, ''));
      fd.append('description', uploadForm.description);
      fd.append('category', uploadForm.category);
      await axios.post(`${API}/api/files/admin/upload`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      setShowUpload(false);
      setUploadForm({ title: '', description: '', category: 'Vehicle Specs', file: null });
      fetchFiles();
    } catch (err) { console.error('Upload failed:', err); alert('Upload failed. Make sure you are logged in as admin.'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/api/files/admin/${fileId}`, { headers: authHeaders() });
      fetchFiles();
    } catch (err) { console.error('Delete failed:', err); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="file-library-page">
      {/* Header */}
      <header className="bg-blue-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <Link to="/" className="text-blue-300 hover:text-white text-sm flex items-center gap-1 mb-4" data-testid="back-home-link">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold">Downloads & Resources</h1>
          <p className="text-blue-200/70 mt-2 text-base max-w-xl">
            Spec sheets, brochures, and compliance documents for our products and services.
          </p>
          {isAdmin && (
            <button onClick={() => setShowUpload(true)} className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 transition-colors" data-testid="upload-file-btn">
              <Plus size={16} /> Upload File
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              data-testid="file-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setActiveCategory('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!activeCategory ? 'bg-blue-950 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'}`}
              data-testid="filter-all"
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat === activeCategory ? '' : cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeCategory === cat ? 'bg-blue-950 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'}`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* File Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-500">No files found</h3>
            <p className="text-gray-400 text-sm mt-1">
              {search ? 'Try a different search term.' : 'No documents have been uploaded yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(file => (
              <div
                key={file.id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group"
                data-testid={`file-card-${file.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                    {fileIcon(file.filename)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">{file.title}</h3>
                    {file.description && (
                      <p className="text-gray-500 text-xs mt-1 line-clamp-2">{file.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">{file.category}</span>
                      <span>{formatSize(file.file_size)}</span>
                      <span className="flex items-center gap-1"><Download size={10} />{file.download_count}</span>
                    </div>
                  </div>
                </div>
                <a
                  href={`${API}/api/files/${file.id}/download`}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-950 hover:bg-blue-900 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
                  data-testid={`download-btn-${file.id}`}
                >
                  <Download size={14} /> Download
                </a>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="mt-2 w-full flex items-center justify-center gap-2 text-red-400 hover:text-red-300 hover:bg-red-950/20 text-xs font-medium py-1.5 rounded-lg transition-colors"
                    data-testid={`delete-btn-${file.id}`}
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer note */}
        {filtered.length > 0 && (
          <p className="text-center text-gray-400 text-xs mt-8">
            {filtered.length} file{filtered.length !== 1 ? 's' : ''} available
            {activeCategory && ` in ${activeCategory}`}
          </p>
        )}
      </div>
      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 relative">
            <button onClick={() => setShowUpload(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><X size={20} /></button>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Upload size={20} className="text-blue-600" /> Upload File</h2>
            <div className="space-y-4">
              <div>
                <label className="text-gray-600 text-xs font-medium mb-1 block">File</label>
                <input
                  type="file"
                  onChange={e => setUploadForm(f => ({ ...f, file: e.target.files[0] }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
                  data-testid="file-input"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium mb-1 block">Title</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Changan CS75 PLUS Specifications"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  data-testid="file-title-input"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium mb-1 block">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of the file..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  data-testid="file-desc-input"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium mb-1 block">Category</label>
                <select
                  value={uploadForm.category}
                  onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  data-testid="file-category-select"
                >
                  <option>Vehicle Specs</option>
                  <option>Brochures</option>
                  <option>Price Lists</option>
                  <option>Compliance Docs</option>
                  <option>General</option>
                </select>
              </div>
              <button
                onClick={handleUpload}
                disabled={!uploadForm.file || uploading}
                className="w-full bg-blue-950 hover:bg-blue-900 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="confirm-upload-btn"
              >
                {uploading ? 'Uploading...' : <><Upload size={16} /> Upload File</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileLibrary;
